import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTrashAlt } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useCloseOnEscape } from '../hooks/useCloseOnEscape.ts';
import { API_BASE } from '../utils/apiEndpoints';

type Props = {
  email: string;
  oauthLinked: boolean;
  t: (key: string) => string;
};

export function PrivacySettings({ email, oauthLinked, t }: Props) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const closeModal = () => {
    if (submitting) return;
    setModalOpen(false);
    setPassword('');
    setConfirmPhrase('');
    setError(null);
  };

  useCloseOnEscape(modalOpen && !submitting, closeModal);

  const expectedPhrase = t('cookiesPolicy.deleteAccount.confirmPhraseValue');

  const handleDelete = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (confirmPhrase.trim() !== expectedPhrase) {
      setError(t('settings.privacyDeletePhraseMismatch'));
      return;
    }
    if (!oauthLinked && !password) {
      setError(t('cookiesPolicy.deleteAccount.error'));
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/api/profile/account`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email,
          password: oauthLinked ? undefined : password,
          confirmPhrase: confirmPhrase.trim(),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(data.error || t('cookiesPolicy.deleteAccount.error'));
        return;
      }
      await logout();
      navigate('/', { replace: true });
    } catch {
      setError(t('cookiesPolicy.deleteAccount.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="settings-container settings-privacy">
      <h1 className="settings-title">{t('settings.sectionPrivacy')}</h1>
      <p className="settings-subtitle">{t('settings.privacyIntro')}</p>

      <section className="settings-privacy-block">
        <div className="settings-privacy-block__head">
          <FaTrashAlt className="settings-privacy-block__icon" aria-hidden />
          <h2 className="settings-privacy-block__title">{t('settings.privacyDataTitle')}</h2>
        </div>
        <ul className="settings-privacy-data-list">
          <li>
            <button
              type="button"
              className="settings-privacy-data-row settings-privacy-data-row--action"
              onClick={() => setModalOpen(true)}
            >
              <span className="settings-privacy-row-icon" aria-hidden>
                <FaTrashAlt />
              </span>
              <span className="settings-privacy-data-row__text">
                <span className="settings-privacy-data-row__title is-danger">
                  {t('settings.privacyDeleteAccount')}
                </span>
                <span className="settings-privacy-data-row__desc">
                  {t('settings.privacyDeleteAccountDesc')}
                </span>
              </span>
            </button>
          </li>
        </ul>
      </section>

      {modalOpen && (
        <div
          className="delete-account-modal-overlay"
          role="presentation"
          onClick={closeModal}
        >
          <div
            className="delete-account-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-account-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="delete-account-title" className="delete-account-modal__title">
              {t('cookiesPolicy.deleteAccount.modalTitle')}
            </h2>
            <form onSubmit={handleDelete} className="delete-account-modal__form">
              {error && <p className="delete-account-modal__error">{error}</p>}
              <label className="delete-account-modal__field">
                {t('cookiesPolicy.deleteAccount.emailLabel')}
                <input type="email" value={email} disabled />
              </label>
              {!oauthLinked && (
                <label className="delete-account-modal__field">
                  {t('cookiesPolicy.deleteAccount.passwordLabel')}
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                </label>
              )}
              {oauthLinked && (
                <p className="delete-account-modal__hint">{t('settings.privacyDeleteOauthHint')}</p>
              )}
              <label className="delete-account-modal__field">
                {t('cookiesPolicy.deleteAccount.confirmPhraseLabel')}{' '}
                <strong>{expectedPhrase}</strong>
                <input
                  type="text"
                  value={confirmPhrase}
                  onChange={(e) => setConfirmPhrase(e.target.value)}
                  placeholder={t('cookiesPolicy.deleteAccount.confirmPhrasePlaceholder')}
                  required
                />
              </label>
              <div className="delete-account-modal__actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                  disabled={submitting}
                >
                  {t('cookiesPolicy.deleteAccount.cancel')}
                </button>
                <button
                  type="submit"
                  className="btn delete-account-modal__submit"
                  disabled={submitting}
                >
                  {t('cookiesPolicy.deleteAccount.submitButton')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
